import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../../../api/axiosInstance";
import { backendUrl } from "../../../api/constants";
import Input from "../../../components/Input/Input";
import { FieldMessage } from "../../../components/Input/InputError";
import KitButton from "../../../components/kit/Button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "../../../context/UserContext/UserProvider";
import AppStatusScreen from "../../../components/AppStatusScreen/AppStatusScreen";

const verificationInputStyle = { textAlign: "center" };

const useMessageFromSearchParams = () => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  const messageKey = searchParams.get("message");

  const messageMap = {
    invalid_token: "invalid, please send another verification email",
    token_expired:
      "the link has expired, please send another verification email",
  };

  const message = messageMap[messageKey] || messageKey;

  return { status, message };
};

const VerifyEmail = ({ fromPostingPage = false }) => {
  const { user, fetchUser } = useUser();
  const navigate = useNavigate();
  const [email, setEmail] = useState(user?.email || "");
  const [message, setMessage] = useState();
  const [loading, setLoading] = useState(false);
  const [, setSearchParams] = useSearchParams();
  const { status, message: paramMessage } = useMessageFromSearchParams();
  const [emailInput, setEmailInput] = useState(user?.email || "");
  const [sentEmail, setSentEmail] = useState("");
  const [verifiedOverride, setVerifiedOverride] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState();
  const [passwordLoading, setPasswordLoading] = useState(false);
  const passwordRef = useRef();
  const confirmPasswordRef = useRef();

  //if no user is logged in, redirect to login page
  // useEffect(() => {
  //   if (!user && !paramMessage && !isLoading) {
  //     sessionStorage.setItem("redirect_url", `/auth/verify-email?status=${status}`);
  //     navigate("/auth/signin");
  //   }
  // }, [user,paramMessage,isLoading]);

  // Sync email state with user email when it changes
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
      setEmailInput((currentEmail) => currentEmail || user.email);
    }
  }, [user?.email]);

  useEffect(() => {
    if (status === "success" || status === "verified") {
      fetchUser({ supressLoading: true });
    }
  }, [fetchUser, status]);

  const sendVerificationEmail = async () => {
    const targetEmail = (email || emailInput).trim();

    if (!targetEmail) {
      setMessage({ error: "please enter your email address" });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      if (targetEmail !== user?.email) {
        const { data: updateData } = await axiosInstance.post(
          `${backendUrl}/user/me/update`,
          { email: targetEmail }
        );

        if (updateData.error) {
          setMessage({
            error: updateData.validation?.email?.error || updateData.error,
          });
          return;
        }

        await fetchUser({ supressLoading: true });
      }

      const { data } = await axiosInstance.post(
        `${backendUrl}/user/me/verify-email-request`,
        { email: targetEmail }
      );

      if (data.error) {
        setMessage({ error: data.validation?.email?.error || data.error });
      } else {
        setEmail(targetEmail);
        setEmailInput(targetEmail);
        setSentEmail(data.email || targetEmail);
        setMessage(null);
      }
    } catch (error) {
      console.log(error);
      setMessage({ error: "something went wrong, please try again later" });
    } finally {
      setLoading(false);
    }
  };

  const continueAfterSending = async () => {
    try {
      setLoading(true);
      setMessage(null);
      const refreshedUser = await fetchUser({ supressLoading: true });

      if (refreshedUser?.emailVerified) {
        setVerifiedOverride(true);
      } else {
        setMessage({
          error: "your email is not verified yet.\nclick the email link and then hit continue below",
        });
      }
    } catch (error) {
      console.log(error);
      setMessage({ error: "something went wrong, please try again later" });
    } finally {
      setLoading(false);
    }
  };

  const setGuestPassword = async () => {
    const password = passwordRef.current?.value || "";
    const confirmPassword = confirmPasswordRef.current?.value || "";

    if (password !== confirmPassword) {
      setPasswordMessage({ error: "passwords do not match" });
      return;
    }

    try {
      setPasswordLoading(true);
      setPasswordMessage(null);
      const { data } = await axiosInstance.post(`${backendUrl}/user/me/update`, {
        password,
        confirmPassword,
      });

      if (data.error) {
        setPasswordMessage({
          error: data.validation?.password?.error || data.error,
        });
      } else {
        fetchUser({ supressLoading: true });
        navigate("/", { replace: true });
      }
    } catch (error) {
      console.log(error);
      setPasswordMessage({ error: "something went wrong, please try again later" });
    } finally {
      setPasswordLoading(false);
    }
  };

  const verified =
    verifiedOverride || status === "success" || user?.emailVerified || status === "verified";
  const isReusedVerifiedLink =
    status === "error" && paramMessage === "invalid, please send another verification email" && user?.emailVerified;
  const showVerificationError = status === "error" && !isReusedVerifiedLink;
  const mustSetGuestPassword = verified && user?.isGuest;
  const showCheckEmail = sentEmail && !verified;

  if (showVerificationError) {
    return (
      <AppStatusScreen
        title="verify email"
        actionsClassName="mx-auto max-w-[12.2rem]"
        subtitle={<FieldMessage>{paramMessage || "something went wrong, please try again"}</FieldMessage>}
        actions={
          <KitButton
            variant="primary"
            fullWidth
            onClick={() => {
              setSearchParams({});
              setMessage(null);
              setSentEmail("");
            }}
          >
            try again
          </KitButton>
        }
      />
    );
  }

  if (mustSetGuestPassword) {
    return (
      <AppStatusScreen
        title="email verified."
        subtitle="last step, create a password"
        centerClassName="mt-kit-20"
        actionsClassName="mx-auto w-full max-w-[16rem]"
        actions={
          <KitButton variant="primary" fullWidth loading={passwordLoading} onClick={setGuestPassword}>
            set password
          </KitButton>
        }
      >
        <div className="inyc-fields mt-kit-40 flex w-full max-w-[25rem] flex-col gap-kit-16">
          <Input
            Ref={passwordRef}
            autoComplete="new-password"
            placeHolder="input password"
            password
            className="!mt-0"
            inputStyle={verificationInputStyle}
          />
          <Input
            Ref={confirmPasswordRef}
            autoComplete="new-password"
            placeHolder="confirm password"
            password
            className="!mt-0"
            inputStyle={verificationInputStyle}
          />
        </div>
        <div className="inyc-help is-sm mt-kit-20 w-full max-w-[18rem] text-left">
          <div>passwords need to include at least:</div>
          <div>- 8-24 characters</div>
          <div>- 1 uppercase letter</div>
          <div>- 1 lowercase letter</div>
          <div>- 1 number</div>
        </div>
        {passwordMessage && (
          <FieldMessage className="!mx-auto mt-kit-16 w-fit" message={passwordMessage} />
        )}
      </AppStatusScreen>
    );
  }

  if (verified) {
    return (
      <AppStatusScreen
        title="email verified."
        subtitle="now you're able to post."
        actionsClassName="mx-auto w-full max-w-[12.2rem]"
        actions={
          <KitButton variant="primary" fullWidth onClick={() => navigate("/", { replace: true })}>
            continue
          </KitButton>
        }
      />
    );
  }

  if (showCheckEmail) {
    return (
      <AppStatusScreen
        title="check your email"
        body={
          <p>
            verification link sent to:<br />
            <span className="!text-[var(--inyc-primary-text-color)]">{sentEmail}</span>
          </p>
        }
        actions={
          <div className="flex w-fit flex-col gap-kit-12">
            <KitButton
              variant="tertiary"
              fullWidth
              onClick={() => {
                setSentEmail("");
                setMessage(null);
              }}
            >
              use a different email
            </KitButton>
            <KitButton variant="tertiary" fullWidth loading={loading} onClick={sendVerificationEmail}>
              resend email
            </KitButton>
            <KitButton variant="primary" fullWidth disabled={loading} onClick={continueAfterSending}>
              continue
            </KitButton>
          </div>
        }
      >
        {message && (
          <FieldMessage className="!mx-auto mt-kit-20 w-fit" message={message} />
        )}
      </AppStatusScreen>
    );
  }

  return (
    <AppStatusScreen
      title="verify email"
      subtitle={
        fromPostingPage ? (
          <>
            we need to verify your email
            <br />
            before you can post
          </>
        ) : email ? (
          <>
            send a verification email to <span className="!text-[var(--inyc-primary-text-color)]">{email}</span>
          </>
        ) : (
          "enter your email address and we'll send a verification link"
        )
      }
      actions={
        <div className="flex w-fit flex-col gap-kit-12">
          <KitButton variant="tertiary" fullWidth onClick={() => navigate("/", { replace: true })}>
            back to map
          </KitButton>
          <KitButton variant="primary" fullWidth loading={loading} onClick={sendVerificationEmail}>
            send verification email
          </KitButton>
        </div>
      }
    >
      <div className="inyc-fields mt-kit-32 w-full max-w-[22rem]">
        <Input
          placeHolder="input email"
          value={emailInput}
          onChange={(e) => {
            setEmailInput(e.target.value);
            setEmail(e.target.value);
          }}
          autoComplete="email"
          type="email"
          name="email"
          className="!mt-0"
          inputStyle={verificationInputStyle}
        />
      </div>
      {message && <FieldMessage className="mt-kit-16" message={message} />}
    </AppStatusScreen>
  );
};

export default VerifyEmail;
